import { isLikelyValidEmail, isValidCPF, isValidFullName, suggestEmailFix } from "../../../shared/validation.js";
import { isValidPhoneE164 } from "../../../shared/phone.js";

export type CheckoutField = "fullName" | "email" | "phone" | "cpf";

/** What barred Pay. Also the suffix of the Clarity event, so keep these stable. */
export type CheckoutValidationCode = "full_name" | "email" | "email_typo" | "phone" | "cpf";

export interface CheckoutFieldError {
  field: CheckoutField;
  code: CheckoutValidationCode;
  /** The corrected email, for `email_typo` only. */
  suggestion?: string;
}

/** DOM ids of the inputs, so a barred Pay can scroll to the field and focus it. */
export const CHECKOUT_FIELD_IDS: Record<CheckoutField, string> = {
  fullName: "checkout-full-name",
  email: "checkout-email",
  phone: "checkout-phone",
  cpf: "checkout-cpf",
};

export const CHECKOUT_FIELD_MESSAGE_KEYS: Record<CheckoutValidationCode, string> = {
  full_name: "checkout.errors.fullName",
  email: "checkout.errors.email",
  email_typo: "checkout.errors.emailTypo",
  phone: "checkout.errors.phone",
  cpf: "checkout.errors.cpf",
};

/**
 * zenpay's own checks, in form order, after the browser's `required` bubble has passed. Returns
 * the first failure, which is the field the buyer has to fix next. `email` must already be
 * normalized.
 */
export function validateCheckoutFields(input: {
  fullName: string;
  email: string;
  phone: string;
  cpf: string;
  needsCpf: boolean;
}): CheckoutFieldError | null {
  if (!isValidFullName(input.fullName)) return { field: "fullName", code: "full_name" };
  if (!isLikelyValidEmail(input.email)) return { field: "email", code: "email" };
  const suggestion = suggestEmailFix(input.email);
  if (suggestion) return { field: "email", code: "email_typo", suggestion };
  if (!isValidPhoneE164(input.phone)) return { field: "phone", code: "phone" };
  if (input.needsCpf && !isValidCPF(input.cpf)) return { field: "cpf", code: "cpf" };
  return null;
}

/** The server repeats the same checks; its refusals land on the same field. */
export function checkoutFieldErrorFromApiCode(code: string | undefined): CheckoutFieldError | null {
  switch (code) {
    case "invalid_full_name":
      return { field: "fullName", code: "full_name" };
    case "invalid_email":
      return { field: "email", code: "email" };
    case "invalid_phone":
      return { field: "phone", code: "phone" };
    case "invalid_cpf":
      return { field: "cpf", code: "cpf" };
    default:
      return null;
  }
}

/** Clarity event for a barred Pay, e.g. `checkout_invalid_cpf`. */
export function checkoutInvalidEventName(code: CheckoutValidationCode): string {
  return `checkout_invalid_${code}`;
}

const CHECKOUT_FIELD_CODES: Record<CheckoutField, string> = {
  fullName: "full_name",
  email: "email",
  phone: "phone",
  cpf: "cpf",
};

/**
 * Clarity event for a Pay the browser itself barred (empty field, malformed email). The bubble is
 * the browser's; this only records which field it was, by the input id.
 */
export function checkoutNativeInvalidEventName(inputId: string): string | null {
  const field = (Object.keys(CHECKOUT_FIELD_IDS) as CheckoutField[]).find(
    (key) => CHECKOUT_FIELD_IDS[key] === inputId,
  );
  if (!field) return null;
  return `checkout_native_invalid_${CHECKOUT_FIELD_CODES[field]}`;
}

/**
 * The browser fires `invalid` on every invalid control of the form at once, then shows its bubble on
 * the first one. Only that first control is what barred Pay, so only it is reported.
 */
export function nativeInvalidEventFor(target: Element, form: ParentNode): string | null {
  if (form.querySelector(":invalid") !== target) return null;
  return checkoutNativeInvalidEventName(target.id);
}

/**
 * Bring the field into view and put the cursor in it. `center` keeps it clear of the fixed Pay bar
 * on phones, which would otherwise cover a field scrolled to the bottom edge. Focus runs inside the
 * submit handler, so iOS still opens the keyboard.
 */
export function revealCheckoutField(field: CheckoutField, doc: Document = document): void {
  const el = doc.getElementById(CHECKOUT_FIELD_IDS[field]);
  if (!el) return;
  if (!(el as HTMLInputElement).disabled) el.focus({ preventScroll: true });
  el.scrollIntoView({ block: "center", behavior: "smooth" });
}
